import { inject, Injectable } from '@angular/core';
import { ReplyTemplate } from './reply-tab-group/template-selection/template-selection.component';
import Delta from 'quill-delta';
import { Review } from '../../models/review';
import { getEnumValues } from '../../core/utilities';
import { QuillEditorComponent } from 'ngx-quill';
import { Store } from '@ngrx/store';
import { ApiService } from '../../core/api-service';
import { FeedbackService } from '../../core/feedback.service';
import { TranslateService } from '@ngx-translate/core';
import { selectAccountID } from '../../store/global/core-selectors';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DuplicateNameStrategy } from '../../models/duplicate-name-strategy';
import { ReplyTemplatesActions } from './state/review-actions';

export interface TagListItem {
  id: number,
  value: string,
  delimitedValue: string
}

export enum TagName {
  BUSINESS_NAME = 'Business_Name',
  REVIEWER_NAME = 'Reviewer_Name'
}

export enum TagDelimiter {
  TAG_START = '{{',
  TAG_END = '}}'
}

@Injectable()
export class ReplyTemplateService {
  private readonly store = inject(Store);
  private readonly apiService = inject(ApiService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private readonly accountId = this.store.selectSignal(selectAccountID);

  tagNames = getEnumValues(TagName);

  constructor() {
  }

  /**
   * Saves the edited or newly created template and updates it in the template list.
   * Dispatches notifications and handles errors natively.
   */
  saveReplyTemplate(changedTemplate: ReplyTemplate): Observable<ReplyTemplate | null> {
    const accountId = this.accountId();
    return this.apiService.saveReplyTemplate(changedTemplate, accountId)
        .pipe(
            map((res) => {
              if (res?.result) {
                res.result['templateObject'] = this.generateQuillDelta(res.result['templateText']);
                return res.result as ReplyTemplate;
              } else {
                return null;
              }
            }),
            catchError((err: HttpErrorResponse) => {
              console.error(err);
              this.feedbackService.showAlertSnackbar({
                data: {
                  userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.ERROR_SAVING'),
                },
              }, err?.status);
              return EMPTY;
            }),
        );
  }

  /**
   * Deletes the template and updates the template list.
   * Displays the confirmation dialog natively.
   */
  deleteReplyTemplate(template: ReplyTemplate): Observable<boolean> {
    return this.feedbackService.showConfirmationPopup({
      data: {
        title: this.translate.instant('REVIEWS.TEMPLATES.DELETE_TEMPLATE?', { templateName: template.templateName }),
        text: this.translate.instant('REVIEWS.TEMPLATES.DELETE_TEMPLATE_INFO'),
        submitButtonText: this.translate.instant('DELETE'),
        cancelButtonText: this.translate.instant('CANCEL'),
      },
      disableClose: true,
    }).pipe(
        switchMap((shouldDelete) => {
          if (shouldDelete) {
            const accountId = this.accountId();
            return this.apiService.deleteReplyTemplate(template.id, accountId).pipe(
                map(() => true),
                catchError((err: HttpErrorResponse) => {
                  console.error(err);
                  this.feedbackService.showAlertSnackbar({
                    data: {
                      userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.ERROR_DELETING'),
                    },
                  }, err?.status);
                  return EMPTY;
                }),
            );
          }
          return EMPTY;
        }),
    );
  }

  /**
   * Upload CSV reply templates and handle duplicate warnings, snackbars, and routing.
   * Emits the response upon success, and passes errors so the caller can reset loaders.
   */
  uploadReplyTemplates(file: File, duplicateNameStrategy?: DuplicateNameStrategy): Observable<any> {
    const accountId = this.accountId();
    return this.apiService.uploadReplyTemplates(file, accountId, duplicateNameStrategy).pipe(
        tap((res) => {
          this.feedbackService.showSuccessSnackbar({
            data: {
              userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.UPLOAD_SUCCESSFUL'),
            },
          });
          this.store.dispatch(ReplyTemplatesActions.getReplyTemplatesSuccess({ replyTemplates: res?.result }));
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);

          if (err?.error?.message === 'DUPLICATE_TEMPLATE_TITLE') {
            // in case of duplicate template titles -> warning (let the user choose to skip or overwrite)
            this.feedbackService.showWarningSnackbar({
              data: {
                userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.ERROR_UPLOAD_DUPLICATE_TITLES'),
                actionButtons: [
                  {
                    text: this.translate.instant('REVIEWS.TEMPLATES.UPLOAD_SKIP_DUPLICATES'),
                    action: () => this.uploadReplyTemplates(file, DuplicateNameStrategy.SKIP).subscribe(),
                  },
                  {
                    text: this.translate.instant('REVIEWS.TEMPLATES.UPLOAD_OVERWRITE_DUPLICATES'),
                    action: () => this.uploadReplyTemplates(file, DuplicateNameStrategy.OVERWRITE).subscribe(),
                  },
                ],
                dismissButton: {
                  text: this.translate.instant('CANCEL'),
                },
              },
            });
          } else if (['INVALID_SCHEMA', 'NO_CSV'].includes(err?.error?.message)) {
            // in case of invalid schema -> error with action button routing to LSC guide
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: this.translate.instant('REVIEWS.TEMPLATES.ERROR_UPLOAD_INVALID_SCHEMA'),
                actionButtons: [{
                  text: this.translate.instant('SEE_LSC_GUIDE'),
                  action: () => this.router.navigateByUrl('/guide'),
                }],
                dismissButton: {
                  text: this.translate.instant('CLOSE'),
                },
              },
            }, err.status);
          } else {
            // other errors
            const errorMessage = this.translate.instant('REVIEWS.TEMPLATES.ERROR_UPLOAD');
            this.feedbackService.showAlertSnackbar({ data: { userFriendlyMessage: errorMessage } }, err.status);
          }

          throw err;
        }),
    );
  }

  /**
   * Checks if there have been changes to the templateName or templateText.
   * If yes, automatically prompts the user with the "Discard draft?" pop-up and yields the choice.
   */
  confirmCancel(formGroup: FormGroup, originalTemplate: ReplyTemplate): Observable<boolean> {
    const templateNameNew: string = formGroup.get('templateName')?.value || '';
    const templateNameOld: string = originalTemplate?.templateName || '';

    const templateTextNew: string = formGroup.get('templateText')?.value || '';
    const templateTextOld: string = originalTemplate?.templateText || '';

    if (((templateNameNew || templateNameOld) && (templateNameNew != templateNameOld)) ||
        ((templateTextNew || templateTextOld) && (templateTextNew != templateTextOld))) {
      return this.feedbackService.showConfirmationPopup({
        data: {
          title: this.translate.instant('REVIEWS.TEMPLATES.DISCARD_DRAFT?'),
          text: this.translate.instant('REVIEWS.TEMPLATES.DISCARD_DRAFT_INFO'),
          submitButtonText: this.translate.instant('DISCARD'),
          cancelButtonText: this.translate.instant('CANCEL'),
        },
        disableClose: true,
      });
    }
    return of(true);
  }

  /**
   * Extracts text out of a Quill Delta Object
   * If mentions are found, removes delimiters and '_', i.e. sets the display value for Quill editor
   * @param replyObject https://github.com/quilljs/delta
   * {
   *   ops: [
   *     { insert: 'White', attributes: { color: '#fff' } },
   *     { insert: mentions: { id: 1, value: 'Business Name' } }
   *   ]
   * }
   */
  generateReplyTextFromDelta(replyObject: Delta): string {
    let replyText = '';
    replyObject?.ops?.forEach(deltaOperation => {
      if (deltaOperation.insert && typeof deltaOperation.insert === 'string') {
        replyText = replyText + deltaOperation.insert;
      } else if (deltaOperation.insert && deltaOperation.insert['mention']) {
        replyText = replyText
            + TagDelimiter.TAG_START
            + deltaOperation.insert['mention'].value.replace(' ', '_')
            + TagDelimiter.TAG_END;
      }
    });
    return replyText;
  }

  /**
   * Generates a Quill Delta Object out of plain text
   * Mentions will be identified by delimiters and translated back to Delta elements with attributes
   * needed for tag highlighting in Quill editor
   * @param plainText
   */
  generateQuillDelta(plainText: string): Delta {
    let plainTextWithSpacesOnly = plainText;
    const quillDelta = new Delta();

    // 1. Identify all matches, tag name and starting index
    const matches = this.getReplacementsList(plainText);

    // 2. Generate Delta Operations
    let previousSearchStringEndIndex = 0;
    matches.forEach((match) => {
      const matchIndexInPlainText = match.startIndex;
      const tagContentInPlainText = match.searchStr;

      // 1. Insert plain text before tag match
      if (matchIndexInPlainText !== previousSearchStringEndIndex) {
        quillDelta.insert(plainTextWithSpacesOnly.slice(previousSearchStringEndIndex, matchIndexInPlainText));
      }

      // 2. Insert tag
      const rawTagName = tagContentInPlainText.slice(TagDelimiter.TAG_START.length, tagContentInPlainText.length - TagDelimiter.TAG_END.length);
      const formattedTagName = rawTagName.replace('_', ' ');

      const idOfTagElement = this.tagNames.indexOf(rawTagName);

      quillDelta.insert({
        mention: {
          id: idOfTagElement,
          value: formattedTagName,
          denotationChar: '@',
        },
      });

      previousSearchStringEndIndex = matchIndexInPlainText + tagContentInPlainText.length;
    });

    // 3. Insert remaining plain text (after the last match)
    if (previousSearchStringEndIndex !== plainText.length) {
      quillDelta.insert(plainTextWithSpacesOnly.slice(previousSearchStringEndIndex, plainText.length));
    }

    return quillDelta;
  }

  /**
   * Generates an array of all tag names with ID and value needed for mentions menu
   */
  generateTagList(): TagListItem[] {
    const list: TagListItem[] = [];
    this.tagNames.forEach((tagName, index) => {
      list.push({
        id: index,
        value: tagName.replace('_', ' '),
        delimitedValue: TagDelimiter.TAG_START + tagName + TagDelimiter.TAG_END,
      });
    });
    return list;
  }

  /**
   * Replaces tag delimiters in plain text with actual data
   * @param plainText
   * @param review
   */
  replaceTagsWithRealData(plainText: string, review: Review): string {
    const replacementsList = this.getReplacementsList(plainText);

    let plainTextWithReplacements = plainText;
    replacementsList.forEach((tagMatch) => {
      const tagNameWithDelimiters = tagMatch.searchStr;
      const rawTagName = tagNameWithDelimiters.slice(TagDelimiter.TAG_START.length, tagMatch.searchStr.length - TagDelimiter.TAG_END.length);

      let valueOfTagReplacement: string;
      switch (rawTagName) {
        case TagName.BUSINESS_NAME:
          valueOfTagReplacement = review.businessName;
          break;
        case TagName.REVIEWER_NAME:
          valueOfTagReplacement = review.reviewerName;
          break;
        default:
          valueOfTagReplacement = tagNameWithDelimiters;
          break;
      }
      plainTextWithReplacements = plainTextWithReplacements.replace(tagNameWithDelimiters, valueOfTagReplacement);
    });

    return plainTextWithReplacements;
  }

  /**
   * Replaces actual business data in plain text with tags (best effort, case insensitive, replaces first match)
   * used when saving custom reply as draft to provide a better UX.
   * @param plainText
   * @param review
   */
  replaceRealDataWithTags(plainText: string, review: Review): string {
    let replyTextWithReplacedData = plainText;

    if (review?.reviewerName) {
      const reviewerNameRegExp = new RegExp(review.reviewerName, 'i');
      replyTextWithReplacedData = replyTextWithReplacedData.replace(reviewerNameRegExp, TagDelimiter.TAG_START + TagName.REVIEWER_NAME + TagDelimiter.TAG_END);
    }

    if (review?.businessName) {
      const businessNameRegExp = new RegExp(review.businessName, 'i');
      replyTextWithReplacedData = replyTextWithReplacedData.replace(businessNameRegExp, TagDelimiter.TAG_START + TagName.BUSINESS_NAME + TagDelimiter.TAG_END);
    }

    return replyTextWithReplacedData;
  }

  /**
   * Check if a template uses any tags and return their tag names.
   * @param template
   */
  getUsedReplyTags(template: ReplyTemplate): string[] {
    const list = this.getReplacementsList(template.templateText);
    const uniqueMatchesSet = new Set<string>();
    list.forEach(match => {
      uniqueMatchesSet.add(match.searchStr);
    });
    return Array.from(uniqueMatchesSet);
  }

  private getReplacementsList(plainText: string) {
    let list = [];
    this.tagNames.forEach((tagName) => {
      const formattedTagName = TagDelimiter.TAG_START + tagName + TagDelimiter.TAG_END;
      list = list.concat(this.getIndicesOf(formattedTagName, plainText));
    });

    list.sort((a, b) => a.startIndex - b.startIndex);
    return list;
  }

  private getIndicesOf(searchStr, str) {
    const searchStrLen = searchStr.length;
    if (searchStrLen === 0) {
      return [];
    }
    let startIndex = 0,
        index,
        indices = [];
    while ((index = str.indexOf(searchStr, startIndex)) > -1) {
      indices.push({ searchStr: searchStr, startIndex: index });
      startIndex = index + searchStrLen;
    }
    return indices;
  }

  /**
   * Wraps all matched tags into a <span class="mention"> element, which will allow highlighting
   * them with CSS
   * @param templateText
   */
  generateTemplateInnerHTML(templateText: string): string {
    let innerHTMLString = templateText;

    this.generateTagList()?.forEach(tag => {
      const span = `<span class="mention">${tag.value}</span>`;
      while (innerHTMLString
          .indexOf(tag.delimitedValue) > -1) {
        innerHTMLString = innerHTMLString.replace(tag.delimitedValue, span);
      }
    });
    return innerHTMLString;
  }

  /**
   * Insert emoji into Quill editor programmatically
   * @param emoji
   * @param quillEditorRef
   */
  insertEmoji(emoji: string, quillEditorRef: QuillEditorComponent) {
    if (quillEditorRef?.quillEditor?.hasFocus()) {
      const range = quillEditorRef.quillEditor.getSelection(true);
      if (range) {
        quillEditorRef.quillEditor.insertText(range.index, emoji, 'api');
      }
    } else {
      // no focus - refocus and run again
      quillEditorRef.quillEditor.focus();
      this.insertEmoji(emoji, quillEditorRef);
    }
  }
}
